import { byId, isAr, message, trackEvent } from './extra-common.js';
import { soundEngine } from '../core/audio-engine.js';
import { showToast } from '../core/modal.js';

let hasStarted = false;

const catalog = {
  length: {
    m: 1,
    km: 1000,
    cm: 0.01,
    mm: 0.001,
    mi: 1609.344,
    yd: 0.9144,
    ft: 0.3048,
    in: 0.0254
  },
  mass: {
    kg: 1,
    g: 0.001,
    mg: 0.000001,
    lb: 0.45359237,
    oz: 0.028349523125,
    t: 1000,
    ct: 0.0002
  },
  temp: {
    C: 1,
    F: 1,
    K: 1
  },
  data: {
    bit: 1,
    byte: 8,
    kB: 8000,
    KiB: 8192,
    MB: 8e6,
    MiB: 8388608,
    GB: 8e9,
    GiB: 8589934592,
    TB: 8e12,
    TiB: 8796093022208
  },
  speed: {
    kmh: 1,
    ms: 3.6,
    mph: 1.609344,
    knot: 1.852
  },
  time: {
    s: 1,
    min: 60,
    h: 3600,
    d: 86400,
    wk: 604800,
    yr: 31536000
  }
};

const names = {
  en: {
    m: 'Meters (m)',
    km: 'Kilometers (km)',
    cm: 'Centimeters (cm)',
    mm: 'Millimeters (mm)',
    mi: 'Miles (mi)',
    yd: 'Yards (yd)',
    ft: 'Feet (ft)',
    in: 'Inches (in)',
    kg: 'Kilograms (kg)',
    g: 'Grams (g)',
    mg: 'Milligrams (mg)',
    lb: 'Pounds (lb)',
    oz: 'Ounces (oz)',
    t: 'Metric tonnes (t)',
    ct: 'Carats (ct)',
    bit: 'Bits',
    byte: 'Bytes',
    kB: 'Kilobytes (SI)',
    KiB: 'Kibibytes (IEC)',
    MB: 'Megabytes (SI)',
    MiB: 'Mebibytes (IEC)',
    GB: 'Gigabytes (SI)',
    GiB: 'Gibibytes (IEC)',
    TB: 'Terabytes (SI)',
    TiB: 'Tebibytes (IEC)',
    C: 'Celsius (°C)',
    F: 'Fahrenheit (°F)',
    K: 'Kelvin (K)',
    kmh: 'Kilometers/hour (km/h)',
    ms: 'Meters/second (m/s)',
    mph: 'Miles/hour (mph)',
    knot: 'Knots (kn)',
    s: 'Seconds (s)',
    min: 'Minutes (min)',
    h: 'Hours (h)',
    d: 'Days (d)',
    wk: 'Weeks (wk)',
    yr: 'Years (yr)'
  },
  ar: {
    m: 'متر (m)',
    km: 'كيلومتر (km)',
    cm: 'سنتيمتر (cm)',
    mm: 'ملليمتر (mm)',
    mi: 'ميل (mi)',
    yd: 'ياردة (yd)',
    ft: 'قدم (ft)',
    in: 'بوصة (in)',
    kg: 'كيلوجرام (kg)',
    g: 'جرام (g)',
    mg: 'ملليجرام (mg)',
    lb: 'رطل (lb)',
    oz: 'أونصة (oz)',
    t: 'طن متري (t)',
    ct: 'قيراط (ct)',
    bit: 'بت (bit)',
    byte: 'بايت (byte)',
    kB: 'كيلوبايت (SI)',
    KiB: 'كيبيبايت (IEC)',
    MB: 'ميجابايت (SI)',
    MiB: 'ميبيبايت (IEC)',
    GB: 'جيجابايت (SI)',
    GiB: 'جيبيبايت (IEC)',
    TB: 'تيرابايت (SI)',
    TiB: 'تيبيبايت (IEC)',
    C: 'مئوية (°C)',
    F: 'فهرنهايت (°F)',
    K: 'كلفن (K)',
    kmh: 'كيلومتر/ساعة (km/h)',
    ms: 'متر/ثانية (m/s)',
    mph: 'ميل/ساعة (mph)',
    knot: 'عقدة بحرية (knot)',
    s: 'ثواني (s)',
    min: 'دقائق (min)',
    h: 'ساعات (h)',
    d: 'أيام (d)',
    wk: 'أسابيع (wk)',
    yr: 'سنوات (yr)'
  }
};

// Aliases for natural language parser
const UNIT_ALIASES = {
  // length
  m: ['m', 'meter', 'meters', 'متر', 'امتار', 'أمتار'],
  km: ['km', 'kilometer', 'kilometers', 'كيلو', 'كيلومتر', 'كم'],
  cm: ['cm', 'centimeter', 'centimeters', 'سم', 'سنتيمتر'],
  mm: ['mm', 'millimeter', 'millimeters', 'ملم', 'ملليمتر'],
  mi: ['mi', 'mile', 'miles', 'ميل', 'اميال', 'أميال'],
  yd: ['yd', 'yard', 'yards', 'ياردة', 'ياردات'],
  ft: ['ft', 'feet', 'foot', 'قدم', 'اقدام', 'أقدام'],
  in: ['in', 'inch', 'inches', 'بوصة', 'انش', 'إنش'],
  // mass
  kg: ['kg', 'kilo', 'kilogram', 'kilograms', 'كيلوجرام', 'كجم', 'كيلو'],
  g: ['g', 'gram', 'grams', 'جرام', 'غرام'],
  mg: ['mg', 'milligram', 'milligrams', 'ملغ', 'ملليجرام'],
  lb: ['lb', 'lbs', 'pound', 'pounds', 'رطل', 'باوند'],
  oz: ['oz', 'ounce', 'ounces', 'اونصة', 'أونصة'],
  t: ['t', 'tonne', 'ton', 'tons', 'طن'],
  // temp
  C: ['c', 'celsius', 'مئوي', 'مئوية', 'سلسيوس'],
  F: ['f', 'fahrenheit', 'فهرنهايت'],
  K: ['k', 'kelvin', 'كلفن'],
  // data
  bit: ['bit', 'bits', 'بت'],
  byte: ['byte', 'bytes', 'بايت'],
  kB: ['kb', 'كيلوبايت'],
  MB: ['mb', 'ميجابايت'],
  GB: ['gb', 'جيجابايت'],
  TB: ['tb', 'تيرابايت'],
  // speed
  kmh: ['kmh', 'km/h', 'kph', 'كم/س'],
  ms: ['ms', 'm/s', 'م/ث'],
  mph: ['mph', 'ميل/س'],
  // time
  s: ['s', 'sec', 'second', 'seconds', 'ثانية', 'ثواني'],
  min: ['min', 'minute', 'minutes', 'دقيقة', 'دقائق'],
  h: ['h', 'hr', 'hour', 'hours', 'ساعة', 'ساعات'],
  d: ['d', 'day', 'days', 'يوم', 'أيام', 'ايام']
};

function populate() {
  const catEl = byId('uc-category');
  const category = (catEl && catEl.value && catalog[catEl.value]) ? catEl.value : 'length';
  const units = Object.keys(catalog[category]);

  for (const side of ['from', 'to']) {
    const select = byId(`uc-${side}`);
    if (!select) continue;
    const previous = select.value;
    select.replaceChildren(...units.map((unit) => new Option(names[isAr ? 'ar' : 'en'][unit], unit)));
    if (units.includes(previous)) select.value = previous;
  }

  const fromEl = byId('uc-from');
  const toEl = byId('uc-to');
  if (fromEl && toEl && fromEl.value === toEl.value) {
    toEl.selectedIndex = Math.min(1, units.length - 1);
  }
  convert();
}

function tempToC(value, unit) {
  return unit === 'F' ? (value - 32) * 5 / 9 : unit === 'K' ? value - 273.15 : value;
}

function fromC(value, unit) {
  return unit === 'F' ? value * 9 / 5 + 32 : unit === 'K' ? value + 273.15 : value;
}

function updateFormula(category, from, to, value, result) {
  const formulaEl = byId('uc-formula');
  if (!formulaEl) return;

  if (from === to) {
    formulaEl.textContent = isAr ? 'نفس الوحدة (1 : 1)' : 'Identical units (1 : 1)';
    return;
  }

  if (category === 'temp') {
    if (from === 'C' && to === 'F') {
      formulaEl.textContent = `°F = (°C × 9/5) + 32`;
    } else if (from === 'F' && to === 'C') {
      formulaEl.textContent = `°C = (°F - 32) × 5/9`;
    } else if (from === 'C' && to === 'K') {
      formulaEl.textContent = `K = °C + 273.15`;
    } else if (from === 'K' && to === 'C') {
      formulaEl.textContent = `°C = K - 273.15`;
    } else {
      formulaEl.textContent = `K = (°F - 32) × 5/9 + 273.15`;
    }
  } else {
    const catUnits = catalog[category];
    const factor = catUnits[from] / catUnits[to];
    const factorFormatted = new Intl.NumberFormat(isAr ? 'ar' : 'en', { maximumSignificantDigits: 6 }).format(factor);
    formulaEl.textContent = isAr
      ? `المعادلة: اضرب في ${factorFormatted} (1 ${from} = ${factorFormatted} ${to})`
      : `Formula: Multiply by ${factorFormatted} (1 ${from} = ${factorFormatted} ${to})`;
  }
}

function convert() {
  const valEl = byId('uc-value');
  const value = valEl ? Number(valEl.value) : 1;
  const catEl = byId('uc-category');
  const fromEl = byId('uc-from');
  const toEl = byId('uc-to');
  const resEl = byId('uc-result');

  if (!Number.isFinite(value)) {
    if (resEl) resEl.textContent = '—';
    return;
  }

  const category = (catEl && catEl.value && catalog[catEl.value]) ? catEl.value : 'length';
  const catUnits = catalog[category] || catalog.length;
  const unitKeys = Object.keys(catUnits);
  const from = (fromEl && fromEl.value && catUnits[fromEl.value] !== undefined) ? fromEl.value : unitKeys[0];
  const to = (toEl && toEl.value && catUnits[toEl.value] !== undefined) ? toEl.value : (unitKeys[1] || unitKeys[0]);

  const result = category === 'temp'
    ? fromC(tempToC(value, from), to)
    : (value * catUnits[from]) / catUnits[to];

  if (resEl) {
    const formatted = new Intl.NumberFormat(isAr ? 'ar' : 'en', { maximumSignificantDigits: 10 }).format(result);
    const unitName = (names[isAr ? 'ar' : 'en'] && names[isAr ? 'ar' : 'en'][to]) || to;
    resEl.textContent = `${formatted} ${unitName}`;
  }

  updateFormula(category, from, to, value, result);
  message('uc-status', '');

  if (hasStarted) trackEvent('tool_completed', 'unit-converter', { category, from, to });
}

function swapUnits() {
  const fromEl = byId('uc-from');
  const toEl = byId('uc-to');
  if (fromEl && toEl) {
    const tmp = fromEl.value;
    fromEl.value = toEl.value;
    toEl.value = tmp;
    soundEngine.beep(700, 0.03, 0.05);
    convert();
  }
}

function parseNaturalQuery(query) {
  if (!query || typeof query !== 'string') return;
  const cleaned = query.trim().toLowerCase();

  // Pattern: "<value> <from_unit> (to|in|into|إلى|الي) <to_unit>"
  const match = cleaned.match(/^([\d\.]+)\s*([a-zA-Z\/\u0600-\u06FF°]+)\s+(?:to|in|into|إلى|الي)\s+([a-zA-Z\/\u0600-\u06FF°]+)$/);
  if (!match) return;

  const numVal = parseFloat(match[1]);
  const rawFrom = match[2];
  const rawTo = match[3];

  if (isNaN(numVal)) return;

  // Resolve unit aliases
  let foundFrom = null;
  let foundTo = null;
  let foundCategory = null;

  for (const [unitCode, aliases] of Object.entries(UNIT_ALIASES)) {
    if (aliases.includes(rawFrom) || unitCode.toLowerCase() === rawFrom) {
      foundFrom = unitCode;
    }
    if (aliases.includes(rawTo) || unitCode.toLowerCase() === rawTo) {
      foundTo = unitCode;
    }
  }

  if (foundFrom && foundTo) {
    // Find category that contains both
    for (const [catName, catUnits] of Object.entries(catalog)) {
      if (catUnits[foundFrom] !== undefined && catUnits[foundTo] !== undefined) {
        foundCategory = catName;
        break;
      }
    }
  }

  if (foundCategory && foundFrom && foundTo) {
    const catEl = byId('uc-category');
    const valEl = byId('uc-value');
    if (catEl) catEl.value = foundCategory;
    populate();
    const fromEl = byId('uc-from');
    const toEl = byId('uc-to');
    if (fromEl) fromEl.value = foundFrom;
    if (toEl) toEl.value = foundTo;
    if (valEl) valEl.value = String(numVal);
    convert();
    soundEngine.playSuccess();
  }
}

// Event Listeners
const catEl = byId('uc-category');
if (catEl) {
  catEl.addEventListener('change', () => {
    if (!hasStarted) { hasStarted = true; trackEvent('tool_started', 'unit-converter'); }
    populate();
  });
}

const valEl = byId('uc-value');
if (valEl) {
  valEl.addEventListener('input', () => {
    if (!hasStarted) { hasStarted = true; trackEvent('tool_started', 'unit-converter'); }
    convert();
  });
}

const fromEl = byId('uc-from');
if (fromEl) {
  fromEl.addEventListener('change', () => {
    if (!hasStarted) { hasStarted = true; trackEvent('tool_started', 'unit-converter'); }
    convert();
  });
}

const toEl = byId('uc-to');
if (toEl) {
  toEl.addEventListener('change', () => {
    if (!hasStarted) { hasStarted = true; trackEvent('tool_started', 'unit-converter'); }
    convert();
  });
}

const swapBtn = byId('uc-swap-btn');
if (swapBtn) {
  swapBtn.addEventListener('click', swapUnits);
}

const quickInput = byId('uc-quick-input');
if (quickInput) {
  quickInput.addEventListener('input', (e) => {
    parseNaturalQuery(e.target.value);
  });
}

const copyBtn = byId('uc-copy-btn');
if (copyBtn) {
  copyBtn.addEventListener('click', () => {
    const resEl = byId('uc-result');
    if (resEl && resEl.textContent && resEl.textContent !== '—') {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(resEl.textContent).catch(() => {});
      }
      showToast(isAr ? 'تم نسخ النتيجة بنجاح' : 'Result copied to clipboard', { type: 'success' });
    }
  });
}

populate();
